import currency from "currency.js";
import moment from "moment";
import { SideResponse } from "../../codegen-api";
import { COLORS, TEXT_COLORS } from "../../constants/design/colors";
import {
  formatKnownPrice,
  formatKnownValue,
  formatRoiPercent,
  signedTone,
} from "../../utils/unknownNumbers";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { nanosToSeconds } from "../../utils/date";
import {
  formatCurrencyWithSubscript,
  formatSizePrecision,
} from "../../utils/format";
import {
  Separator,
  SettlementInProgress,
} from "../PortfolioSettings/Table/style";
import RowActionButton from "../shared/RowActionButton";
import { Align, SizeFillCell, TPSLCell } from "../shared/Table/style";
import { IModifiedPosition } from "./model";
import { greeksAccessor } from "./OptionPositionTableRow.helpers";
import {
  OptionNameTitle,
  PositionTitleWrapper,
  RowActionWrapper,
} from "./style";

interface ICellDeps {
  actionButtonId: string | undefined;
  inLiquidation: boolean | undefined;
  onClosePosition: any;
  onSharePosition: any;
  onViewPositionDetails: any;
  t: any;
}

export const renderCellContentImpl = (
  pos: IModifiedPosition,
  column: ITableColumn<IModifiedPosition>,
  deps: ICellDeps
) => {
  const {
    actionButtonId,
    inLiquidation,
    onClosePosition,
    onSharePosition,
    onViewPositionDetails,
    t,
  } = deps;
  if (!pos.option) {
    return null;
  }

  const { accessor, align, id } = column;
  const {
    roi,
    unrealized_pnl,
    side,
    option,
    amount,
    amountPrecision,
    maintenance_margin,
  } = pos;
  // First column
  if (accessor === "instrument_name") {

    return (
      <PositionTitleWrapper>
        <Align align={align}>
          <div>
            <OptionNameTitle>
              {currency(option.strike).format({ symbol: "" })}
              <Separator />
              {t(option.option_type)}
            </OptionNameTitle>
            {Boolean(
              moment
                .unix(nanosToSeconds(option.expiry))
                .isSameOrBefore(moment())
            ) && (
              <SettlementInProgress>
                {t("settlement_in_progress")}
              </SettlementInProgress>
            )}
          </div>
        </Align>
      </PositionTitleWrapper>
    );
  }

  // Special case
  if (greeksAccessor.includes(accessor as any)) {
    const a = (accessor as string)?.split(".")[1];
    const val = (pos.option as any)[a as any];
    return (
      <Align align={align}>
        {a === "theta"
          ? currency(val || 0).format()
          : Number(val || 0).toFixed(2)}
      </Align>
    );
  }

  if (actionButtonId && id === actionButtonId) {
    return (
      <RowActionWrapper>
        {!inLiquidation && (
          <RowActionButton
            variant={"close"}
            onClick={(e) => {
              e.stopPropagation();
              onClosePosition(pos);
            }}
          />
        )}
        <RowActionButton
          variant={"details"}
          onClick={(e) => {
            e.stopPropagation();
            onViewPositionDetails(pos);
          }}
        />
        <RowActionButton
          variant="share"
          svgColor={signedTone(roi, {
            up: COLORS.up.one,
            down: COLORS.down.one,
            unknown: TEXT_COLORS.three,
          })}
          onClick={(e) => {
            e.stopPropagation();
            onSharePosition(pos);
          }}
        />
      </RowActionWrapper>
    );
  }

  let item: JSX.Element | null = null;
  switch (accessor) {
    case "side": {
      item = (
        <span
          style={{
            color:
              side === SideResponse.Buy
                ? COLORS.up.one
                : COLORS.down.one,
          }}
        >
          {t(side)}
        </span>
      );
      break;
    }
    case "amount": {
      item = <span>{formatSizePrecision(amount, amountPrecision)}</span>;
      break;
    }
    case "mark_price": {
      item = (
        <span>{formatKnownPrice(pos.mark_price, pos.pricePrecision)}</span>
      );
      break;
    }
    case "positionValue": {
      item = (
        <span>{formatKnownValue(pos.positionValue, pos.pricePrecision)}</span>
      );
      break;
    }
    case "avg_entry_price": {
      item = (
        <span>
          {currency(pos[accessor]?.toString() || 0, {
            precision: pos.pricePrecision,
          }).format()}
        </span>
      );
      break;
    }
    case "unrealized_pnl": {
      const color = signedTone(unrealized_pnl, {
        up: COLORS.up.one,
        down: COLORS.down.one,
        unknown: TEXT_COLORS.three,
      });
      const roiColor = signedTone(roi, {
        up: COLORS.up.two,
        down: COLORS.down.two,
        unknown: TEXT_COLORS.three,
      });

      item = (
        <SizeFillCell>
          <span style={{ color }}>
            {formatCurrencyWithSubscript(unrealized_pnl, {
              showPositiveSign: true,
            })}
          </span>
          {roi !== undefined && roi !== null ? (
            <span style={{ color: roiColor }}>{formatRoiPercent(roi)}</span>
          ) : null}
        </SizeFillCell>
      );
      break;
    }
    case "triggers": {
      item = (
        <TPSLCell>
          <span>-</span>
        </TPSLCell>
      );
      break;
    }
    case "maintenance_margin": {
      item = <span>{formatKnownValue(maintenance_margin)}</span>;
      break;
    }
    default: {
      item = (
        <span>
          {(accessor
            ? (pos as any)[accessor as string] || "-"
            : "-"
          ).toString()}
        </span>
      );
      break;
    }
  }

  return <Align align={align}>{item}</Align>;
};
